import { store, persistor } from "@/redux/store";
// A pure hostname read with no imports of its own, so it cannot reintroduce the
// api ⇄ authService ⇄ forceLogout cycle this module is careful to avoid.
import { getTenantSlug } from "@/utils/tenantSlug";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
const THEME_KEY = "app-theme";
const DEPLOYMENT_ID_KEY = "__deployment_id";

let inFlight = false;

// Expire every cookie readable by JS. The httpOnly refresh_token cookie cannot
// be touched here — the server /logout call below clears it.
const clearClientCookies = () => {
  if (typeof document === "undefined" || !document.cookie) return;
  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name) return;
    document.cookie = `${name}=; ${expiry}; path=/`;
    document.cookie = `${name}=; ${expiry}; path=/; domain=${window.location.hostname}`;
  });
};

// Drop any Cache Storage entries. No service worker today, but this future-proofs
// against a cached app shell surviving a deploy.
const clearCacheStorage = async () => {
  if (typeof caches === "undefined") return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    // best-effort — never block logout on cache eviction
  }
};

/**
 * Hard logout: wipe every trace of the session on the client and redirect to
 * login. Used both for explicit logout failure paths and when the backend
 * reports a DEPLOYMENT_RESTART (force re-login after a new deployment).
 *
 * Imports only the store (not AuthReducer / api) and dispatches the action by
 * its string type to avoid the api ⇄ authService ⇄ forceLogout import cycle.
 *
 * Optional `reason` is appended to the destination URL as `?reason=<value>`
 * so the login page can show a contextual toast (e.g. "org_suspended").
 * Query params survive the hard navigation without any storage gymnastics.
 */
export const forceFullLogout = ({ reason } = {}) => {
  if (inFlight) return;
  inFlight = true;

  // Preserve the user's theme across the wipe so the login screen isn't jarring.
  const theme = typeof localStorage !== "undefined" ? localStorage.getItem(THEME_KEY) : null;

  // Tell the server to revoke the refresh row and expire the httpOnly cookie.
  // Fire-and-forget via fetch (no apiService import → no circular dependency).
  //
  // X-Tenant-Slug has to be here even though /logout no longer requires it: the
  // slug picks the *engine*, and a dedicated tenant's session rows live on that
  // tenant's own database. Without it the server looks on the platform engine,
  // finds nothing, and answers 200 — leaving a live session row behind.
  try {
    fetch(`${API_BASE_URL}/api/v2/auth/logout`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "X-Tenant-Slug": getTenantSlug() },
    }).catch(() => { });
  } catch {
    // ignore — proceed with the client-side wipe regardless
  }

  // redux-persist blob (persist:talentflow)
  try {
    persistor.purge();
  } catch {
    // ignore
  }

  try {
    store.dispatch({ type: "auth/removeAuth" });
  } catch {
    // ignore
  }

  if (typeof localStorage !== "undefined") {
    localStorage.clear();
    if (theme) localStorage.setItem(THEME_KEY, theme);
    // Keep the current deployment id so the deploymentGuard does not purge again
    // on the next load (we just logged out for this exact deployment).
    const currentDeploymentId = import.meta.env.VITE_DEPLOYMENT_ID || "dev";
    localStorage.setItem(DEPLOYMENT_ID_KEY, currentDeploymentId);
  }

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.clear();
  }

  clearClientCookies();

  // Cache eviction is async; redirect immediately after kicking it off. A full
  // navigation (not SPA push) guarantees a clean store and the freshest bundle.
  clearCacheStorage();

  if (typeof window !== "undefined") {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    window.location.replace(`/login${query}`);
  }
};

export default forceFullLogout;
